import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  ArrowLeft, ArrowRight, Check, FileText, Download, Printer,
  Ticket, Eye, Droplets, Heart, Handshake, Gift, Sparkles, Mail, MessageCircle, Send
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const TYPE_ICONS = { lavado: Droplets, cortesia: Heart, atencion_comercial: Handshake, cheque_regalo: Gift, personalizado: Sparkles };

export default function VoucherIssue() {
  const { axiosAuth } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [templates, setTemplates] = useState([]);
  const [centers, setCenters] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null); // { code: 'forbidden'|'unauthorized'|'server', message: string }
  const [issuing, setIssuing] = useState(false);
  const [issuedVoucher, setIssuedVoucher] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [whatsappSent, setWhatsappSent] = useState(false);

  const [form, setForm] = useState({
    client_name: "", client_phone: "", client_email: "", plate: "",
    center: "", expiry_days: "", observations: "", internal_reason: "",
    estimated_value: "",
  });

  useEffect(() => {
    Promise.all([
      axiosAuth().get("/vouchers/templates"),
      axiosAuth().get("/vouchers/config"),
    ]).then(([tR, cR]) => {
      setTemplates((tR.data.templates || []).filter(t => t.active));
      setCenters(cR.data.centers || []);
      setLoadError(null);
      setLoading(false);
    }).catch((err) => {
      const st = err?.response?.status;
      const detail = err?.response?.data?.detail || "";
      if (st === 401) {
        setLoadError({ code: "unauthorized", message: "Tu sesión ha expirado. Vuelve a iniciar sesión." });
      } else if (st === 403) {
        setLoadError({
          code: "forbidden",
          message: detail || "No tienes permisos para cargar plantillas de bonos. Pide a un administrador que añada el permiso 'ver' sobre el módulo 'atenciones_comerciales' a tu rol.",
        });
      } else {
        setLoadError({
          code: "server",
          message: detail || "Error al cargar datos. Inténtalo de nuevo en unos segundos.",
        });
        toast.error("Error al cargar datos");
      }
      setLoading(false);
    });
  }, [axiosAuth]);

  const selectTemplate = (tpl) => {
    setSelectedTemplate(tpl);
    setForm(f => ({
      ...f,
      expiry_days: tpl.default_expiry_days?.toString() || "30",
      estimated_value: tpl.default_value?.toString() || "0",
    }));
    setStep(2);
  };

  const handleIssue = async () => {
    if (!selectedTemplate) return;
    if (!form.client_name.trim()) { toast.error("Nombre del cliente obligatorio"); return; }
    setIssuing(true);
    try {
      const payload = {
        template_id: selectedTemplate.id,
        client_name: form.client_name,
        client_phone: form.client_phone,
        client_email: form.client_email,
        plate: form.plate,
        center: form.center,
        expiry_days: parseInt(form.expiry_days) || selectedTemplate.default_expiry_days || 30,
        observations: form.observations,
        internal_reason: form.internal_reason,
        estimated_value: parseFloat(form.estimated_value) || selectedTemplate.default_value || 0,
        status: "emitido",
      };
      const r = await axiosAuth().post("/vouchers/", payload);
      setIssuedVoucher(r.data);
      toast.success(`Bono ${r.data.code} emitido correctamente`);
      setStep(4);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al emitir bono");
    } finally { setIssuing(false); }
  };

  const downloadPdf = async () => {
    if (!issuedVoucher) return;
    try {
      const r = await axiosAuth().get(`/vouchers/pdf/${issuedVoucher.id}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url;
      a.download = `bono_${issuedVoucher.code.replace(/-/g, "_")}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Error al descargar PDF"); }
  };

  const printPdf = async () => {
    if (!issuedVoucher) return;
    try {
      const r = await axiosAuth().get(`/vouchers/pdf/${issuedVoucher.id}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const win = window.open(url);
      if (win) win.onload = () => win.print();
    } catch { toast.error("Error al imprimir"); }
  };

  const handleSendEmail = async () => {
    if (!issuedVoucher || !form.client_email) return;
    setSendingEmail(true);
    try {
      await axiosAuth().post(`/vouchers/${issuedVoucher.id}/send-email`, {
        to_email: form.client_email,
        subject: `Tu bono Ascauto — ${issuedVoucher.code}`,
        body: "",
      });
      toast.success(`PDF enviado por email a ${form.client_email}`);
      setEmailSent(true);
    } catch { toast.error("Error al enviar email"); }
    finally { setSendingEmail(false); }
  };

  const handleSendWhatsapp = async () => {
    if (!issuedVoucher) return;
    let phone = (form.client_phone || "").replace(/\s/g, "").replace(/[^0-9+]/g, "");
    if (!phone) { toast.error("No hay telefono del cliente"); return; }
    if (!phone.startsWith("+")) phone = "+34" + phone;
    const msg = encodeURIComponent(`Hola${form.client_name ? ` ${form.client_name}` : ""}, le enviamos su bono de atencion comercial de Ascauto (${issuedVoucher.code}). Puede canjearlo hasta el ${issuedVoucher.expiry_date || "la fecha indicada"}. Gracias por su confianza.`);
    window.open(`https://wa.me/${phone.replace("+", "")}?text=${msg}`, "_blank");
    try {
      await axiosAuth().post(`/vouchers/${issuedVoucher.id}/whatsapp-event`, { phone: form.client_phone, message: "Envio post-emision" });
    } catch {}
    toast.success("WhatsApp abierto. Descarga el PDF para adjuntarlo.");
    setWhatsappSent(true);
  };

  const upd = (field, value) => setForm(f => ({ ...f, [field]: value }));

  if (loading) return <div className="p-8 text-center text-sm text-slate-400">Cargando...</div>;

  return (
    <div className="space-y-6" data-testid="voucher-issue">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => step > 1 && step < 4 ? setStep(s => s - 1) : navigate("/admin/atenciones/bonos")} className="h-9 w-9 p-0 rounded-sm">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div>
          <h1 className="heading-industrial text-2xl font-bold text-slate-900 uppercase tracking-tight">Emitir Bono</h1>
          <p className="text-sm text-slate-500 mt-0.5">Paso {step} de 4</p>
        </div>
      </div>

      {/* Steps indicator */}
      <div className="flex items-center gap-2">
        {["Plantilla", "Datos", "Vista previa", "Emitido"].map((s, i) => (
          <div key={i} className="flex items-center gap-1.5 flex-1">
            <div className={`h-1.5 flex-1 rounded-full transition-colors ${step > i + 1 ? "bg-emerald-500" : step === i + 1 ? "bg-slate-900" : "bg-slate-200"}`} />
            <span className={`text-[9px] uppercase tracking-[0.12em] font-bold whitespace-nowrap ${step === i + 1 ? "text-slate-900" : step > i + 1 ? "text-emerald-600" : "text-slate-400"}`}>{s}</span>
          </div>
        ))}
      </div>

      {/* STEP 1: Select template */}
      {step === 1 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {templates.map(tpl => {
            const Icon = TYPE_ICONS[tpl.type] || Ticket;
            return (
              <Card key={tpl.id} className="border-slate-200 rounded-sm cursor-pointer hover:border-slate-400 hover:shadow-sm transition-all overflow-hidden"
                onClick={() => selectTemplate(tpl)} data-testid={`tpl-${tpl.type}`}>
                <div className="h-1" style={{ backgroundColor: tpl.bar_color || "#0f172a" }} />
                <div className="h-0.5" style={{ backgroundColor: tpl.accent_color || "#dc2626" }} />
                <CardContent className="p-5">
                  <div className="flex items-start gap-3">
                    <div className="w-11 h-11 rounded-sm flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: `${tpl.accent_color}12`, color: tpl.accent_color }}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-slate-900">{tpl.name}</p>
                      <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{tpl.description_text}</p>
                      <div className="flex items-center gap-2 mt-2.5">
                        <Badge variant="outline" className="h-5 text-[9px] rounded-sm border-slate-200 text-slate-500 font-semibold">
                          {tpl.default_expiry_days} dias
                        </Badge>
                        <Badge variant="outline" className="h-5 text-[9px] rounded-sm border-slate-200 text-slate-500 font-mono font-semibold">
                          {tpl.default_value}€
                        </Badge>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
          {templates.length === 0 && (
            <div className="col-span-full text-center py-14" data-testid="voucher-issue-empty-state">
              {loadError?.code === "forbidden" ? (
                <>
                  <Ticket className="w-10 h-10 text-red-300 mx-auto mb-3" />
                  <p className="text-sm font-semibold text-red-700" data-testid="voucher-issue-forbidden">
                    No tienes permisos para cargar plantillas de bonos.
                  </p>
                  <p className="text-xs text-slate-500 mt-1 max-w-md mx-auto">{loadError.message}</p>
                  <Button variant="outline" onClick={() => navigate("/admin/atenciones")} className="mt-4 rounded-sm text-xs uppercase tracking-wider" data-testid="voucher-issue-back-btn">
                    <ArrowLeft className="w-3.5 h-3.5 mr-1.5" /> Volver
                  </Button>
                </>
              ) : loadError?.code === "unauthorized" ? (
                <>
                  <Ticket className="w-10 h-10 text-amber-300 mx-auto mb-3" />
                  <p className="text-sm font-semibold text-amber-700">{loadError.message}</p>
                  <Button variant="outline" onClick={() => navigate("/login")} className="mt-4 rounded-sm text-xs uppercase tracking-wider">
                    Iniciar sesión
                  </Button>
                </>
              ) : loadError?.code === "server" ? (
                <>
                  <Ticket className="w-10 h-10 text-red-300 mx-auto mb-3" />
                  <p className="text-sm font-semibold text-red-700" data-testid="voucher-issue-server-error">
                    {loadError.message}
                  </p>
                  <Button variant="outline" onClick={() => window.location.reload()} className="mt-4 rounded-sm text-xs uppercase tracking-wider">
                    Reintentar
                  </Button>
                </>
              ) : (
                <>
                  <Ticket className="w-10 h-10 text-slate-200 mx-auto mb-3" />
                  <p className="text-sm text-slate-400" data-testid="voucher-issue-empty">No hay plantillas activas. Crea una plantilla primero.</p>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* STEP 2: Fill data */}
      {step === 2 && selectedTemplate && (
        <Card className="border-slate-200 rounded-sm overflow-hidden">
          <div className="h-1" style={{ backgroundColor: selectedTemplate.bar_color || "#0f172a" }} />
          <div className="h-0.5" style={{ backgroundColor: selectedTemplate.accent_color || "#dc2626" }} />
          <CardHeader className="pb-3 pt-5">
            <CardTitle className="text-xs uppercase tracking-[0.15em] font-bold text-slate-500 flex items-center gap-2">
              <FileText className="w-4 h-4 text-red-600" /> Datos del bono — {selectedTemplate.name}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 pb-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-[10px] uppercase tracking-[0.12em] font-bold text-slate-600">Nombre del cliente *</Label>
                <Input value={form.client_name} onChange={e => upd("client_name", e.target.value)}
                  placeholder="Carlos Garcia" className="rounded-sm h-9 border-slate-200 mt-1" data-testid="client-name-input" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-[0.12em] font-bold text-slate-600">Matricula</Label>
                <Input value={form.plate} onChange={e => upd("plate", e.target.value.toUpperCase())}
                  placeholder="1234ABC" className="rounded-sm h-9 border-slate-200 font-mono uppercase mt-1" data-testid="plate-input" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-[0.12em] font-bold text-slate-600">Telefono</Label>
                <Input value={form.client_phone} onChange={e => upd("client_phone", e.target.value)}
                  placeholder="612345678" className="rounded-sm h-9 border-slate-200 mt-1" data-testid="phone-input" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-[0.12em] font-bold text-slate-600">Email</Label>
                <Input value={form.client_email} onChange={e => upd("client_email", e.target.value)}
                  placeholder="cliente@email.com" className="rounded-sm h-9 border-slate-200 mt-1" data-testid="email-input" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-[0.12em] font-bold text-slate-600">Centro emisor</Label>
                <Select value={form.center || "none"} onValueChange={v => upd("center", v === "none" ? "" : v)}>
                  <SelectTrigger className="rounded-sm h-9 border-slate-200 mt-1" data-testid="center-select"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin especificar</SelectItem>
                    {centers.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-[0.12em] font-bold text-slate-600">Dias de validez</Label>
                <Input type="number" value={form.expiry_days} onChange={e => upd("expiry_days", e.target.value)}
                  className="rounded-sm h-9 border-slate-200 w-24 mt-1" data-testid="expiry-input" />
              </div>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.12em] font-bold text-slate-600">Valor estimado (EUR)</Label>
              <Input type="number" step="0.01" value={form.estimated_value} onChange={e => upd("estimated_value", e.target.value)}
                className="rounded-sm h-9 border-slate-200 w-32 mt-1" data-testid="value-input" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.12em] font-bold text-slate-600">Observaciones (visible en el bono)</Label>
              <Textarea value={form.observations} onChange={e => upd("observations", e.target.value)}
                placeholder="Lavado pendiente del dia..." className="rounded-sm border-slate-200 mt-1" rows={2} data-testid="obs-input" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.12em] font-bold text-slate-600">Motivo interno (no aparece en el bono)</Label>
              <Input value={form.internal_reason} onChange={e => upd("internal_reason", e.target.value)}
                placeholder="Incidencia #123" className="rounded-sm h-9 border-slate-200 mt-1" data-testid="reason-input" />
            </div>
            <div className="flex justify-end pt-2">
              <Button onClick={() => { if (!form.client_name.trim()) { toast.error("Nombre obligatorio"); return; } setStep(3); }}
                className="bg-slate-900 text-white hover:bg-slate-800 rounded-sm text-xs uppercase tracking-wider h-10 px-6 font-semibold" data-testid="next-btn">
                Vista previa <ArrowRight className="w-3.5 h-3.5 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* STEP 3: Preview */}
      {step === 3 && selectedTemplate && (
        <div className="space-y-5">
          <Card className="border-slate-200 rounded-sm">
            <CardContent className="p-6">
              <div className="max-w-xl mx-auto border border-slate-200 rounded-sm overflow-hidden shadow-sm" data-testid="voucher-preview">
                <div className="h-2" style={{ backgroundColor: selectedTemplate.bar_color || "#0f172a" }} />
                <div className="h-0.5" style={{ backgroundColor: selectedTemplate.accent_color || "#dc2626" }} />
                <div className="p-6 space-y-4">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-bold text-slate-400 uppercase tracking-[0.15em]">ASCAUTO</p>
                    <p className="text-xs font-mono text-slate-400">ASC-ATC-XXXX-XXXXXX</p>
                  </div>
                  <h2 className="text-xl font-bold uppercase tracking-tight" style={{ color: selectedTemplate.bar_color || "#0f172a" }}>
                    {selectedTemplate.title}
                  </h2>
                  {selectedTemplate.subtitle && <p className="text-sm text-slate-500">{selectedTemplate.subtitle}</p>}
                  <div className="h-px bg-slate-200" />
                  <div className="grid grid-cols-2 gap-4 text-xs">
                    {[
                      { label: "Cliente", value: form.client_name },
                      { label: "Matricula", value: form.plate, mono: true },
                      { label: "Telefono", value: form.client_phone },
                      { label: "Email", value: form.client_email },
                      { label: "Centro", value: form.center },
                      { label: "Validez", value: `${form.expiry_days} dias` },
                    ].map((f, i) => (
                      <div key={i}>
                        <p className="text-[9px] uppercase tracking-[0.12em] text-slate-400 font-bold">{f.label}</p>
                        <p className={`font-semibold ${f.value ? "text-slate-800" : "text-slate-300"} ${f.mono ? "font-mono" : ""}`}>{f.value || "—"}</p>
                      </div>
                    ))}
                  </div>
                  {form.observations && (
                    <div className="text-xs"><p className="text-[9px] uppercase tracking-[0.12em] text-slate-400 font-bold">Observaciones</p><p className="text-slate-700 mt-0.5">{form.observations}</p></div>
                  )}
                  <div className="bg-slate-50 border border-slate-200 rounded-sm p-3 text-[10px] text-slate-500">
                    <p className="font-bold text-[9px] uppercase tracking-[0.12em] text-slate-400 mb-0.5">Condiciones</p>
                    {selectedTemplate.conditions}
                  </div>
                </div>
                <div className="h-0.5" style={{ backgroundColor: selectedTemplate.accent_color || "#dc2626" }} />
                <div className="h-1" style={{ backgroundColor: selectedTemplate.bar_color || "#0f172a" }} />
              </div>
            </CardContent>
          </Card>
          <div className="flex justify-between">
            <Button variant="outline" onClick={() => setStep(2)} className="rounded-sm text-xs uppercase tracking-wider h-10 px-5 font-semibold">
              <ArrowLeft className="w-3.5 h-3.5 mr-2" /> Modificar datos
            </Button>
            <Button onClick={handleIssue} disabled={issuing}
              className="bg-emerald-600 text-white hover:bg-emerald-700 rounded-sm text-xs uppercase tracking-wider h-10 px-6 font-semibold" data-testid="issue-btn">
              <Check className="w-3.5 h-3.5 mr-2" /> {issuing ? "Emitiendo..." : "Emitir bono"}
            </Button>
          </div>
        </div>
      )}

      {/* STEP 4: Issued */}
      {step === 4 && issuedVoucher && (
        <Card className="border-emerald-200 rounded-sm overflow-hidden">
          <div className="h-1 bg-emerald-500" />
          <CardContent className="p-10 text-center space-y-5">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto">
              <Check className="w-8 h-8 text-emerald-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-900">Bono emitido correctamente</h2>
              <p className="text-2xl font-mono font-bold text-emerald-700 mt-2" data-testid="issued-code">{issuedVoucher.code}</p>
              <p className="text-sm text-slate-500 mt-1">{issuedVoucher.client_name} — {issuedVoucher.plate} — Caduca: {issuedVoucher.expiry_date}</p>
            </div>

            {/* Quick actions */}
            <div className="flex items-center justify-center gap-3 flex-wrap">
              <Button onClick={downloadPdf} variant="outline" className="rounded-sm text-xs uppercase tracking-wider h-10 px-5 font-semibold" data-testid="download-pdf-btn">
                <Download className="w-3.5 h-3.5 mr-2" /> Descargar PDF
              </Button>
              <Button onClick={printPdf} variant="outline" className="rounded-sm text-xs uppercase tracking-wider h-10 px-5 font-semibold" data-testid="print-btn">
                <Printer className="w-3.5 h-3.5 mr-2" /> Imprimir
              </Button>
              <Button onClick={() => navigate(`/admin/atenciones/detalle/${issuedVoucher.id}`)}
                variant="outline" className="rounded-sm text-xs uppercase tracking-wider h-10 px-5 font-semibold">
                <Eye className="w-3.5 h-3.5 mr-2" /> Ver detalle
              </Button>
            </div>

            {/* Send options */}
            <div className="border-t border-emerald-200 pt-5 space-y-3">
              <p className="text-[10px] uppercase tracking-[0.15em] font-bold text-slate-400">Enviar al cliente</p>
              <div className="flex items-center justify-center gap-3 flex-wrap">
                {form.client_email && (
                  <Button onClick={handleSendEmail} disabled={sendingEmail || emailSent}
                    variant="outline" className={`rounded-sm text-xs uppercase tracking-wider h-10 px-5 font-semibold ${emailSent ? "text-emerald-600 border-emerald-300 bg-emerald-50" : "text-violet-700 border-violet-200 hover:bg-violet-50"}`}
                    data-testid="issue-send-email-btn">
                    {emailSent ? <><Check className="w-3.5 h-3.5 mr-2" /> Email enviado</> : sendingEmail ? "Enviando..." : <><Mail className="w-3.5 h-3.5 mr-2" /> Enviar por email</>}
                  </Button>
                )}
                {form.client_phone && (
                  <Button onClick={handleSendWhatsapp} disabled={whatsappSent}
                    variant="outline" className={`rounded-sm text-xs uppercase tracking-wider h-10 px-5 font-semibold ${whatsappSent ? "text-emerald-600 border-emerald-300 bg-emerald-50" : "text-green-700 border-green-200 hover:bg-green-50"}`}
                    data-testid="issue-send-whatsapp-btn">
                    {whatsappSent ? <><Check className="w-3.5 h-3.5 mr-2" /> WhatsApp preparado</> : <><MessageCircle className="w-3.5 h-3.5 mr-2" /> Enviar por WhatsApp</>}
                  </Button>
                )}
                {!form.client_email && !form.client_phone && (
                  <p className="text-xs text-slate-400">No se ha proporcionado email ni telefono del cliente</p>
                )}
              </div>
            </div>

            <div className="pt-3 border-t border-emerald-200">
              <Button onClick={() => { setStep(1); setSelectedTemplate(null); setIssuedVoucher(null); setEmailSent(false); setWhatsappSent(false); setForm({ client_name: "", client_phone: "", client_email: "", plate: "", center: "", expiry_days: "", observations: "", internal_reason: "", estimated_value: "" }); }}
                className="bg-slate-900 text-white hover:bg-slate-800 rounded-sm text-xs uppercase tracking-wider h-10 px-6 font-semibold">
                Emitir otro bono
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
